import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import {
    ClassificationService,
    type Classification,
    type ClassificationOutputType,
    type ClassificationStatus,
} from './ClassificationService';
import { CATEGORY_OPTIONS, defaultOutputConfig } from './ClassificationOptions';
import { IClassificationAssistEngineToken, type IClassificationAssistEngine } from './ClassificationAssistEngine';
import type { ClassificationSnapshot, ClassificationSuggestion, ClassificationSuggestionFields } from '@/shared/assist/ClassificationAssistContract';

export type SaveStatus = 'idle' | 'saving' | 'saved';

/** The editable working copy of a classification shown in the builder. */
export interface ClassificationDraftState {
    category: string;
    name: string;
    description: string;
    instructions: string;
    outputType: ClassificationOutputType;
    outputConfig: Record<string, unknown>;
    status: ClassificationStatus;
}

function emptyDraft(category: string): ClassificationDraftState {
    return {
        category,
        name: '',
        description: '',
        instructions: '',
        outputType: 'scale',
        outputConfig: defaultOutputConfig('scale'),
        status: 'draft',
    };
}

function draftFromDto(dto: Classification): ClassificationDraftState {
    return {
        category: dto.category,
        name: dto.name,
        description: dto.description,
        instructions: dto.instructions,
        outputType: dto.outputType,
        outputConfig: (dto.outputConfig as Record<string, unknown>) ?? {},
        status: dto.status,
    };
}

@injectable()
export class ClassificationPageViewModel extends BaseViewModel {
    classifications: Classification[] = [];
    selectedId: string | null = null;
    /**
     * The category the builder is scoped to. Set from the route; while non-null
     * the category is locked, the list shows only this category, and new
     * classifications inherit it.
     */
    lockedCategory: string | null = null;
    draft: ClassificationDraftState = emptyDraft(CATEGORY_OPTIONS[0]);

    searchQuery = '';
    loading = false;
    saveStatus: SaveStatus = 'idle';
    errorMessage: string | null = null;

    assistPrompt = '';
    assistBusy = false;

    /** AI-assist suggestion review state (mirrors the usage-category assist). */
    assistModalOpen = false;
    suggestions: ClassificationSuggestion[] = [];
    assistNarrative = '';
    hasGenerated = false;
    assistError: string | null = null;
    selectedSuggestionIndices = new Set<number>();

    constructor(
        private readonly service: ClassificationService,
        @inject(IClassificationAssistEngineToken) private readonly assistEngine: IClassificationAssistEngine,
    ) {
        super();
    }

    async initialize(category: string, assistPrompt?: string): Promise<void> {
        this.lockedCategory = category;
        this.loading = true;
        this.notifyChange();
        try {
            this.classifications = await this.service.list();
            if (assistPrompt !== undefined && assistPrompt.trim().length > 0) {
                this.newClassification();
                this.assistPrompt = assistPrompt;
            } else {
                const first = this.classifications.find((c) => c.category === category);
                if (first) {
                    this.selectDraft(first);
                } else {
                    this.newClassification();
                }
            }
        } catch (err) {
            this.errorMessage = toMessage(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    get filteredClassifications(): Classification[] {
        const scoped = this.lockedCategory ? this.classifications.filter((c) => c.category === this.lockedCategory) : this.classifications;
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return scoped;
        return scoped.filter((c) => c.name.toLowerCase().includes(q) || c.category.toLowerCase().includes(q));
    }

    get isNew(): boolean {
        return this.selectedId === null;
    }

    setSearchQuery(value: string): void {
        this.searchQuery = value;
        this.notifyChange();
    }

    select(id: string): void {
        const target = this.classifications.find((c) => c.id === id);
        if (target) this.selectDraft(target);
    }

    newClassification(): void {
        this.selectedId = null;
        this.draft = emptyDraft(this.lockedCategory ?? CATEGORY_OPTIONS[0]);
        this.saveStatus = 'idle';
        this.errorMessage = null;
        this.assistPrompt = '';
        this.notifyChange();
    }

    /** No-op while `lockedCategory` is set — the category is fixed by the surface. */
    setCategory(value: string): void {
        if (this.lockedCategory !== null) return;
        this.draft.category = value;
        this.markDirty();
    }

    setName(value: string): void {
        this.draft.name = value;
        this.markDirty();
    }

    setDescription(value: string): void {
        this.draft.description = value;
        this.markDirty();
    }

    setInstructions(value: string): void {
        this.draft.instructions = value;
        this.markDirty();
    }

    setOutputType(value: ClassificationOutputType): void {
        this.draft.outputType = value;
        this.draft.outputConfig = defaultOutputConfig(value);
        this.markDirty();
    }

    setOutputConfig(patch: Record<string, unknown>): void {
        this.draft.outputConfig = { ...this.draft.outputConfig, ...patch };
        this.markDirty();
    }

    setStatus(value: ClassificationStatus): void {
        this.draft.status = value;
        this.markDirty();
    }

    async save(): Promise<void> {
        if (!this.draft.name.trim() || !this.draft.instructions.trim()) {
            this.errorMessage = 'Name and instructions are required.';
            this.notifyChange();
            return;
        }
        this.saveStatus = 'saving';
        this.errorMessage = null;
        this.notifyChange();
        try {
            const payload = {
                category: this.draft.category,
                name: this.draft.name,
                description: this.draft.description,
                instructions: this.draft.instructions,
                outputType: this.draft.outputType,
                outputConfig: this.draft.outputConfig,
                status: this.draft.status,
            };
            if (this.selectedId) {
                await this.service.update(this.selectedId, payload);
            } else {
                await this.service.create(payload);
            }
            await this.reload(this.selectedId, this.draft.name);
            this.saveStatus = 'saved';
        } catch (err) {
            this.errorMessage = toMessage(err);
            this.saveStatus = 'idle';
        } finally {
            this.notifyChange();
        }
    }

    async remove(id: string): Promise<void> {
        this.saveStatus = 'saving';
        this.notifyChange();
        try {
            await this.service.delete(id);
            await this.reload(null, null);
            this.saveStatus = 'saved';
        } catch (err) {
            this.errorMessage = toMessage(err);
            this.saveStatus = 'idle';
        } finally {
            this.notifyChange();
        }
    }

    setAssistPrompt(value: string): void {
        this.assistPrompt = value;
        this.notifyChange();
    }

    /**
     * Runs the assist for the locked category and opens the review modal with the
     * proposed add/update suggestions (all selected by default — the common
     * "fill in what's missing" case). Errors surface in the inline composer.
     */
    async generateSuggestions(prompt?: string): Promise<void> {
        const text = (prompt ?? this.assistPrompt).trim();
        if (!text || this.assistBusy) return;
        this.assistBusy = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const category = this.lockedCategory ?? this.draft.category;
            const plan = await this.assistEngine.suggest({ prompt: text, category, existing: this.snapshotExisting(category) });
            this.suggestions = plan.suggestions;
            this.assistNarrative = plan.narrative;
            this.hasGenerated = true;
            this.assistError = null;
            this.selectedSuggestionIndices = new Set(plan.suggestions.map((_, i) => i));
            this.assistModalOpen = true;
        } catch (err) {
            this.errorMessage = toMessage(err);
        } finally {
            this.assistBusy = false;
            this.notifyChange();
        }
    }

    private snapshotExisting(category: string): ClassificationSnapshot[] {
        return this.classifications
            .filter((c) => c.category === category)
            .map((c) => ({
                id: c.id,
                name: c.name,
                description: c.description,
                instructions: c.instructions,
                outputType: c.outputType,
                outputConfig: (c.outputConfig as Record<string, unknown>) ?? {},
            }));
    }

    get canApplySuggestions(): boolean {
        return this.selectedSuggestionIndices.size > 0;
    }

    toggleSuggestion(index: number): void {
        const next = new Set(this.selectedSuggestionIndices);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        this.selectedSuggestionIndices = next;
        this.notifyChange();
    }

    setAllSuggestionsSelected(checked: boolean): void {
        this.selectedSuggestionIndices = checked ? new Set(this.suggestions.map((_, i) => i)) : new Set();
        this.notifyChange();
    }

    /** Edits a suggestion's staged fields in place (immutably) before it is applied. */
    setSuggestionField(index: number, patch: ClassificationSuggestionFields): void {
        const current = this.suggestions[index];
        if (!current) return;
        this.suggestions[index] = { ...current, fields: { ...current.fields, ...patch } };
        this.suggestions = [...this.suggestions];
        this.notifyChange();
    }

    /** Switching a suggestion's output type resets its config to that type's default. */
    setSuggestionOutputType(index: number, outputType: ClassificationOutputType): void {
        this.setSuggestionField(index, { outputType, outputConfig: defaultOutputConfig(outputType) });
    }

    closeAssist(): void {
        this.assistModalOpen = false;
        this.resetSuggestions();
        this.notifyChange();
    }

    private resetSuggestions(): void {
        this.suggestions = [];
        this.assistNarrative = '';
        this.hasGenerated = false;
        this.assistError = null;
        this.selectedSuggestionIndices = new Set();
    }

    /**
     * Creates (add) or updates (update) every selected suggestion on the server, then
     * reloads the list and closes the modal. Updates merge the suggestion's changed
     * fields over the existing classification, leaving unspecified fields untouched.
     */
    async applySelectedSuggestions(): Promise<void> {
        const chosen = this.suggestions.filter((_, i) => this.selectedSuggestionIndices.has(i));
        if (chosen.length === 0) return;
        const category = this.lockedCategory ?? this.draft.category;
        this.saveStatus = 'saving';
        this.assistError = null;
        this.notifyChange();
        try {
            let lastName: string | null = null;
            for (const suggestion of chosen) {
                if (suggestion.kind === 'add') {
                    const name = (suggestion.fields.name ?? '').trim();
                    if (name.length === 0) continue;
                    const outputType = suggestion.fields.outputType ?? 'scale';
                    await this.service.create({
                        category,
                        name,
                        description: suggestion.fields.description ?? '',
                        instructions: suggestion.fields.instructions ?? '',
                        outputType,
                        outputConfig: suggestion.fields.outputConfig ?? defaultOutputConfig(outputType),
                        status: 'draft',
                    });
                    lastName = name;
                } else {
                    const existing = this.classifications.find((c) => c.id === suggestion.targetId);
                    if (!existing) continue;
                    const outputType = suggestion.fields.outputType ?? existing.outputType;
                    await this.service.update(suggestion.targetId, {
                        category: existing.category,
                        name: (suggestion.fields.name ?? existing.name).trim() || existing.name,
                        description: suggestion.fields.description ?? existing.description,
                        instructions: suggestion.fields.instructions ?? existing.instructions,
                        outputType,
                        outputConfig: suggestion.fields.outputConfig ?? ((existing.outputConfig as Record<string, unknown>) ?? {}),
                        status: existing.status,
                    });
                    lastName = existing.name;
                }
            }
            this.assistModalOpen = false;
            this.assistPrompt = '';
            this.resetSuggestions();
            await this.reload(null, lastName);
            this.saveStatus = 'saved';
        } catch (err) {
            this.assistError = toMessage(err);
            this.saveStatus = 'idle';
        } finally {
            this.notifyChange();
        }
    }

    private selectDraft(dto: Classification): void {
        this.selectedId = dto.id;
        this.draft = draftFromDto(dto);
        this.saveStatus = 'idle';
        this.errorMessage = null;
        this.notifyChange();
    }

    private async reload(preferId: string | null, preferName: string | null): Promise<void> {
        this.classifications = await this.service.list();
        const scoped = this.lockedCategory ? this.classifications.filter((c) => c.category === this.lockedCategory) : this.classifications;
        const match =
            (preferId ? scoped.find((c) => c.id === preferId) : undefined) ??
            (preferName ? [...scoped].reverse().find((c) => c.name === preferName) : undefined);
        if (match) {
            this.selectDraft(match);
        } else if (scoped[0]) {
            this.selectDraft(scoped[0]);
        } else {
            this.newClassification();
        }
    }

    private markDirty(): void {
        this.saveStatus = 'idle';
        this.notifyChange();
    }
}

function toMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}
